import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { CATEGORY_LABEL, type FlagCategory } from "../lib/reportStore";
import { Icon } from "./icons";
import { toast } from "./toast";

/**
 * Bring markers made in Adobe Audition back in as flags.
 *
 * A reviewer flags in Mockingbird, keeps listening in Audition, drops markers
 * there and exports them. Each export is one audio file's markers; each marker
 * becomes a flag on that file, picked up for the Pickups Package. Its hashtag
 * sets the type (an unknown one means Manual) and the words it quotes are
 * found on the manuscript and highlighted, like any other flag.
 *
 * Nothing is written until "Add flags": the preview is the server's own plan,
 * so what gets added is exactly what was shown. That matters here because the
 * two things most likely to be wrong — which file an export belongs to, and
 * whether the quoted words were found — are both visible before committing.
 */

type Status = "new" | "duplicate" | "error" | "unassigned";

interface PreviewMarker {
  row: number;
  name: string;
  description: string;
  time_text: string;
  start_time: number | null;
  category: FlagCategory;
  tag: string;
  tag_unknown: boolean;
  quote: string;
  anchor: "quote" | "time" | "none";
  anchor_text: string;
  page: number | null;
  status: Status;
  error: string;
}

export interface PreviewFile {
  name: string;
  chapter_id: string | null;
  matched_by_name: boolean;
  error: string;
  warning: string;
  markers: PreviewMarker[];
}

/** An export as picked. `choice`: undefined = match by its name, "" = no
 * file, otherwise the chapter chosen by hand. */
interface Picked {
  key: string;
  name: string;
  b64: string;
  choice?: string;
}

export interface MarkerTarget {
  id: string;
  label: string; // the audio file name, which is what an export is named after
}

async function toBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(bin);
}

/** H:MM:SS.mmm — the same form the pickup exports use. */
function clock(sec: number | null): string {
  if (sec == null) return "–";
  const ms = Math.round(sec * 1000);
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = (ms % 60_000) / 1000;
  return `${h}:${String(m).padStart(2, "0")}:${s.toFixed(3).padStart(6, "0")}`;
}

export function ImportMarkersDialog({ projectId, targets, onClose }: {
  projectId: string;
  targets: MarkerTarget[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<Picked[]>([]);
  const [preview, setPreview] = useState<PreviewFile[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const seq = useRef(0);

  const payload = (list: Picked[]) => ({
    files: list.map((p) => ({ name: p.name, content_b64: p.b64, chapter_id: p.choice })),
  });

  // Re-plan whenever the set of exports or a file choice changes. Only the
  // latest answer counts: choosing two files quickly must not let the first
  // (slower) preview land last.
  useEffect(() => {
    if (!picked.length) { setPreview(null); return; }
    const n = ++seq.current;
    setLoading(true);
    api<{ files: PreviewFile[] }>(`/api/modules/proofing/projects/${projectId}/markers/preview`, {
      method: "POST", body: JSON.stringify(payload(picked)),
    })
      .then((r) => { if (n === seq.current) setPreview(r.files); })
      .catch((e: any) => { if (n === seq.current) toast(e.message || "Couldn't read those files", "error"); })
      .finally(() => { if (n === seq.current) setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, projectId]);

  async function addFiles(list: FileList | File[]) {
    const files = Array.from(list).filter((f) => /\.(csv|txt)$/i.test(f.name) || f.type.startsWith("text/"));
    if (!files.length) { toast("Choose the .csv files Audition exported", "error"); return; }
    const read = await Promise.all(files.map(async (f) => ({
      key: `${f.name}:${f.size}:${f.lastModified}`, name: f.name, b64: await toBase64(f),
    })));
    setPicked((cur) => {
      const have = new Set(cur.map((p) => p.key));
      return [...cur, ...read.filter((r) => !have.has(r.key))];
    });
  }

  function choose(i: number, chapterId: string) {
    setPicked((cur) => cur.map((p, j) => (j === i ? { ...p, choice: chapterId } : p)));
  }
  function remove(i: number) {
    setPicked((cur) => cur.filter((_, j) => j !== i));
  }

  const all = (preview ?? []).flatMap((f) => f.markers);
  const toAdd = all.filter((m) => m.status === "new").length;
  const already = all.filter((m) => m.status === "duplicate").length;
  const unreadable = all.filter((m) => m.status === "error").length
    + (preview ?? []).filter((f) => f.error).length;
  const waiting = (preview ?? []).filter((f) => !f.error && !f.chapter_id).length;

  async function runImport() {
    setImporting(true);
    try {
      const r = await api<{ created: number; already: number; files: number }>(
        `/api/modules/proofing/projects/${projectId}/markers/import`,
        { method: "POST", body: JSON.stringify(payload(picked)) },
      );
      qc.invalidateQueries({ queryKey: ["pickups", projectId] });
      qc.invalidateQueries({ queryKey: ["history"] });
      const files = `${r.files} file${r.files === 1 ? "" : "s"}`;
      toast(
        r.created
          ? `Added ${r.created} flag${r.created === 1 ? "" : "s"} to ${files}${r.already ? ` · ${r.already} were already there` : ""}`
          : "Nothing new to add — those markers are already flags",
        r.created ? "success" : "info",
      );
      onClose();
    } catch (e: any) {
      toast(e.message || "The import didn't go through", "error");
    } finally {
      setImporting(false);
    }
  }

  const drop = {
    onDragOver: (e: React.DragEvent) => { e.preventDefault(); setDragOver(true); },
    onDragLeave: () => setDragOver(false),
    onDrop: (e: React.DragEvent) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); },
  };

  return (
    <div className="rfs-scrim" onClick={() => { if (!importing) onClose(); }}>
      <div className="rfs wide aim" role="dialog" aria-modal="true" aria-labelledby="aim-title"
        onClick={(e) => e.stopPropagation()} {...drop}>
        <div className="rfs-head">
          <div>
            <strong id="aim-title">Import markers from Audition</strong>
            <span>
              Each marker becomes a flag on its audio file, picked up for the Pickups Package. Its
              hashtag sets the type, and the words it quotes are highlighted on the manuscript.
            </span>
          </div>
          {preview && (
            <div className="rfs-chips">
              <span className="rfs-chip ok">{toAdd} to add</span>
              {already > 0 && <span className="rfs-chip mute">{already} already added</span>}
              {unreadable > 0 && <span className="rfs-chip warn">{unreadable} can't be read</span>}
            </div>
          )}
        </div>

        <input ref={inputRef} type="file" multiple hidden accept=".csv,.txt,text/csv,text/plain"
          onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ""; }} />

        {!picked.length ? (
          <div className="aim-start">
            <button type="button" className={`aim-drop${dragOver ? " over" : ""}`} onClick={() => inputRef.current?.click()}>
              <Icon name="upload" size={22} />
              <b>Drop Audition marker exports here</b>
              <span>or click to choose · .csv files, one per audio file</span>
            </button>
            <p className="aim-help">
              In Audition, open the Markers panel, select the markers and choose
              <em> Export Selected Markers to CSV</em>. Name each export after its audio file
              (Audition does this by default) and it lands on that file by itself.
            </p>
          </div>
        ) : (
          <div className={`aim-body${loading ? " loading" : ""}`}>
            {!preview && <div className="aim-wait"><span className="spinner" style={{ width: 16, height: 16 }} /> Reading the markers…</div>}
            {preview?.map((f, i) => (
              <FileSection key={picked[i]?.key ?? i} file={f} targets={targets}
                onChoose={(id) => choose(i, id)} onRemove={() => remove(i)} />
            ))}
          </div>
        )}

        <div className="rfs-foot">
          <div className="rfs-foot-text">
            {picked.length > 0 ? (
              <button type="button" className="btn sm ghost" onClick={() => inputRef.current?.click()} disabled={importing}>
                <Icon name="plus" size={13} /> Add more exports
              </button>
            ) : (
              <>Markers you import show up in each file's report and in the Pickups Package.</>
            )}
            {waiting > 0 && <em className="warn">Choose the audio file for {waiting === 1 ? "one export" : `${waiting} exports`} to include {waiting === 1 ? "it" : "them"}.</em>}
            {picked.length > 0 && !waiting && <em>Importing the same export again won't add anything twice.</em>}
          </div>
          <div className="rfs-actions">
            <button className="btn" onClick={onClose} disabled={importing}>Cancel</button>
            <button className="btn primary" disabled={!toAdd || loading || importing} onClick={runImport}>
              {importing ? "Adding…" : toAdd ? `Add ${toAdd} flag${toAdd === 1 ? "" : "s"}` : "Add flags"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** One export's plan. Without `targets` (flags that already know their file, as Noise Analytics' do) the
 *  heading just names the file: nothing to choose or leave out. */
export function FileSection({ file, targets, onChoose, onRemove }: {
  file: PreviewFile;
  targets?: MarkerTarget[];
  onChoose?: (chapterId: string) => void;
  onRemove?: () => void;
}) {
  return (
    <section className="aim-file">
      <div className="aim-file-head">
        <span className="aim-file-name mono" title={file.name}>{file.name}</span>
        {targets && onChoose && onRemove && (
          <>
            <Icon name="chevron-right" size={14} />
            <select className="rfs-pick aim-pick" value={file.chapter_id ?? ""} disabled={!!file.error}
              onChange={(e) => onChoose(e.target.value)} aria-label={`Audio file for ${file.name}`}>
              <option value="">Choose the audio file…</option>
              {targets.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            {file.matched_by_name && <span className="aim-by-name"><Icon name="check" size={12} /> matched by name</span>}
            <button type="button" className="icon-btn aim-remove" onClick={onRemove} title="Leave this export out" aria-label={`Remove ${file.name}`}>
              <Icon name="x" size={14} />
            </button>
          </>
        )}
      </div>

      {file.error && <p className="aim-msg bad"><Icon name="alert" size={13} /> {file.error}</p>}
      {file.warning && <p className="aim-msg warn"><Icon name="alert" size={13} /> {file.warning}</p>}

      {file.markers.length > 0 && (
        <div className="aim-table" role="table">
          <div className="aim-row aim-th" role="row">
            <span>Time</span><span>Type</span><span>Page</span><span>Highlighted on the manuscript</span><span>Marker</span>
          </div>
          {file.markers.map((m) => <MarkerRow key={m.row} m={m} />)}
        </div>
      )}
    </section>
  );
}

function MarkerRow({ m }: { m: PreviewMarker }) {
  const quoteMissed = !!m.quote && m.anchor === "time";
  const tone =
    m.status === "error" ? "tone-bad"
    : m.status === "duplicate" || m.status === "unassigned" ? "tone-mute"
    : quoteMissed || m.tag_unknown ? "tone-warn"
    : "";
  return (
    <div className={`aim-row ${tone}`} role="row">
      <span className="mono" title={`In Audition: ${m.time_text}`}>{m.start_time != null ? clock(m.start_time) : m.time_text || "–"}</span>
      <span><span className={`aim-type m-${m.category}`}>{CATEGORY_LABEL[m.category] ?? m.category}</span></span>
      <span className="mono">{m.page != null ? m.page : "–"}</span>
      <span className="aim-words">
        {m.anchor_text ? <mark>{m.anchor_text}</mark> : <span className="dim">{m.quote || "–"}</span>}
        {quoteMissed && <em className="warn">“{m.quote}” isn't read near this time, so the words at the marker are highlighted.</em>}
        {!m.quote && m.anchor === "time" && <em>No quoted words, so the words at the marker are highlighted.</em>}
      </span>
      <span className="aim-marker">
        <span className="aim-marker-name">{m.name || <span className="dim">(no name)</span>}</span>
        {m.description && <em>{m.description}</em>}
        {m.status === "duplicate" && <em>Already added</em>}
        {m.status === "unassigned" && <em>Choose the audio file above</em>}
        {m.status === "error" && <em className="bad">{m.error || "Can't be read"}</em>}
        {m.tag_unknown && <em className="warn">#{m.tag} isn't a flag type, so this comes in as Manual.</em>}
      </span>
    </div>
  );
}
